import { formatDate } from "@angular/common";
import { inject, Injectable } from "@angular/core";
import { SigningStatus } from "@models/annual-statements/annual-statement-enums";
import { DocumentRecord } from "@models/annual-statements/document-record";
import { ExportTypeEnum } from "@models/common/export-type.enum";
import { Company } from "@models/companies/company";
import { AsicStatus } from "@models/documents/asic-status.enum";
import { DocumentStatusEnum } from "@models/documents/document-status.enum";
import { RelationshipType, RelationshipTypeLabels } from "@models/relationships/relationship-type.enum";
import {
  CompanyOfficeholdersComponent
} from "@pages/companies/components/company-profile-page/components/company-officeholders/company-officeholders.component";
import { OfficeholderGroupModel } from "@pages/companies/models/officeholder-group-model";
import {
  ExportEntityListModalComponent
} from "@shared/components/commons/export-company-list-modal/export-entity-list-modal.component";
import {
  EntityProfileDocumentsComponent
} from "@shared/components/entity-profiles/entity-profile-documents/entity-profile-documents.component";
import {
  EntitySecurityholdersViewComponent
} from "@shared/components/entity-profiles/entity-securityholders-view/entity-securityholders-view.component";
import {
  SecurityholderViewModel
} from "@shared/components/entity-profiles/entity-securityholders-view/securityholder-view-model";
import {
  documentStatusLabels
} from "@shared/components/grid/ag-cells/common/ag-document-status/document-status-labels";
import { excelReportTitleStyles, excelTitleForReport } from "@shared/utils/excel-title-for-report";
import {
  ColDef,
  ExcelRow,
  ExcelStyle,
  GridApi,
  ProcessCellForExportParams,
  ProcessHeaderForExportParams
} from "ag-grid-community";
import jsPDF from "jspdf";
import autoTable, { Styles } from "jspdf-autotable";
import { take } from "rxjs";
import { ModalFormsService } from "../../../forms/services/modal-forms.service";

@Injectable()
export class CompanyProfileExportService {
  modalFormsService = inject(ModalFormsService);

  exportElements(
    elements: unknown[],
    onlySelected: boolean,
    colDefs: ColDef[],
    exportType: ExportTypeEnum,
    params: Partial<ExportEntityListModalComponent> = {},
    gridApiForExport?: GridApi,
    sheetTitle: string = 'Sheet1',
    titleRows: ExcelRow[] = [],
    companyName: string = 'Report',
    processCellCallback?: (cellParams: ProcessCellForExportParams) => string
  ): void {
    const modalDialogClass = 'export-company-list';
    const modalRef = this.modalFormsService.openModal(
      ExportEntityListModalComponent,
      { modalDialogClass }
    );
    const instance = modalRef.componentInstance as ExportEntityListModalComponent;

    Object.entries(params).forEach(([key, value]) => (instance as any)[key] = value);
    instance.colDefs = colDefs;
    instance.exportColDefs = colDefs.filter((cd, i, self) =>
      cd.headerName != null &&
      i === self.findIndex(c => c.headerName === cd.headerName)
    );
    instance.numberOfCompanies = elements.filter((element) => element && typeof element != 'string').length;
    instance.exportType = exportType;

    instance.confirm
      .pipe(take(1))
      .subscribe((columnsForExport: string[]) => {
        const api = gridApiForExport;
        if (!api) return;

        const debugRows: Record<string, any>[] = elements.map(el => {
          const row: Record<string, any> = {};
          columnsForExport.forEach(key => {
            row[key] = (el as any)[key];
          });
          return row;
        });

        if (exportType === ExportTypeEnum.EXCEL) {
          const groupStyle: ExcelStyle = {
            id: 'groupHeaderStyle',
            interior: {
              color: '#D0CECE',
              pattern: 'Solid'
            },
            font: {
              bold: true
            },
            alignment: {
              horizontal: 'Center'
            }
          };

          const headerStyle = excelReportTitleStyles.find(s => s.id === 'header')!;

          const today = formatDate(new Date(), 'dd-MM-yyyy', 'en-US');
          const fileName = `${ companyName }_${ sheetTitle }_${ today }.xlsx`;

          const allStyles: ExcelStyle[] = [
            ...excelReportTitleStyles,
            groupStyle
          ];

          const cellCallback = processCellCallback
            ? processCellCallback
            : (cellParams: ProcessCellForExportParams) => {
              if (typeof cellParams.node?.data === 'string') {
                if (cellParams.column.getColId() === columnsForExport[0]) {
                  return cellParams.node.data as string;
                }
                return '';
              }
              const rawVal = cellParams.value;
              return rawVal != null ? rawVal.toString() : '';
            };

          const cellStyleCallback = (params: ProcessCellForExportParams): ExcelStyle | undefined => {
            if (typeof params.node?.data === 'string') {
              return { ...groupStyle, alignment: { horizontal: 'Center', vertical: 'Center' } };
            }
            return undefined;
          };


          const excelParams: any = {
            columnKeys: columnsForExport,
            onlySelected,
            sheetName: sheetTitle,
            fileName,
            prependContent: titleRows,
            excelStyles: allStyles,
            getHeaderCellStyle: () => headerStyle,
            processHeaderCallback: (p: ProcessHeaderForExportParams) =>
              p.column.getColDef().headerName ?? '',
            processCellCallback: cellCallback,
            getCellStyle: cellStyleCallback,
            skipHeader: true
          };

          api.exportDataAsExcel(excelParams);
        }
      });
  }

  formatDate(date: Date, format: string = 'dd MMM yyyy'): string {
    const d = date.getDate().toString().padStart(2, '0');
    const m = date.toLocaleString('en-US', { month: 'short' });
    const y = date.getFullYear();
    return format === 'dd MMM yyyy'
      ? `${ d } ${ m } ${ y }`
      : `${ d }.${ (date.getMonth() + 1).toString().padStart(2, '0') }.${ y }`;
  }

  // =====  Officeholders  =====

  exportOfficeholders(
    exportType: ExportTypeEnum,
    companyProfile: Company,
    companyOfficeholdersComponent: CompanyOfficeholdersComponent,
    selectedOfficeholders: OfficeholderGroupModel[],
    onlySelected: boolean
  ): void {
    if (exportType == ExportTypeEnum.EXCEL) {
      this.exportOfficeholdersExcel(
        companyProfile,
        companyOfficeholdersComponent,
        selectedOfficeholders,
        onlySelected
      );
    }
    if (exportType == ExportTypeEnum.PDF) {
      this.exportOfficeholdersPDF(
        companyProfile,
        companyOfficeholdersComponent,
        selectedOfficeholders,
      );
    }
  }

  exportOfficeholdersExcel(companyProfile: Company, companyOfficeholdersComponent: CompanyOfficeholdersComponent, selectedOfficeholders: OfficeholderGroupModel[], onlySelected: boolean): void {
    if (!companyOfficeholdersComponent) return;
    const allRows = (companyOfficeholdersComponent?.officeholdersViewModel().officeholdersViewModel as OfficeholderGroupModel[]) ?? [];

    const params: Partial<ExportEntityListModalComponent> = {
      title: 'Export Company Officeholders',
      subTitle: 'officeholders selected'
    };

    const cols: ColDef[] = companyOfficeholdersComponent?.colDefs() ?? [];
    const columnsForExport = cols
      .map(cd => cd.field ?? '')
      .filter(f => !!f) as string[];

    const titleRows = excelTitleForReport(
      columnsForExport.length - 1,
      'Officeholders Report'
    );

    this.exportElements(
      allRows,
      onlySelected,
      cols,
      ExportTypeEnum.EXCEL,
      params,
      companyOfficeholdersComponent?.companyOfficeholdersGridService.gridApi,
      'Officeholders',
      titleRows,
      companyProfile.name,
      (cellParams: ProcessCellForExportParams) => {
        if (typeof (cellParams.node?.data) === 'string') {
          if (cellParams.column.getColId() === columnsForExport[0]) {
            return cellParams.node.data as string;
          }
          return '';
        }

        const dataNode = cellParams.node?.data as any;
        const colId = cellParams.column.getColId();
        const raw = cellParams.value;

        if (colId === 'types') {
          return (dataNode?.types as RelationshipType[] | null ?? []).map((type) => RelationshipTypeLabels[type]).join(', ') ?? '';
        }
        if (colId === 'address.normalizedFullAddress') {
          return dataNode?.address?.normalizedFullAddress ?? '';
        }
        if (colId === 'birthCity') {
          const country = dataNode.birthCountry ?? '';
          const city = dataNode.birthCity ?? '';
          return country && city ? `${ country }, ${ city }` : city || country || '';
        }
        if (colId === 'din') {
          return dataNode?.din ? dataNode.din.toString() : '';
        }
        if (raw instanceof Date || (typeof raw === 'string' && !isNaN(Date.parse(raw)))) {
          const dt = raw instanceof Date ? raw : new Date(raw);
          return this.formatDate(dt);
        }
        return raw != null ? raw.toString() : '';
      }
    );
  }

  exportOfficeholdersPDF(companyProfile: Company, companyOfficeholdersComponent: CompanyOfficeholdersComponent, selectedOfficeholders: OfficeholderGroupModel[]): void {
    if (!companyOfficeholdersComponent) return;

    const allRows = (companyOfficeholdersComponent?.officeholdersViewModel().officeholdersViewModel as OfficeholderGroupModel[]) ?? [];
    const cols: ColDef[] = companyOfficeholdersComponent?.colDefs() ?? [];

    const modalDialogClass = 'export-company-list';
    const modalRef = this.modalFormsService.openModal(
      ExportEntityListModalComponent,
      { modalDialogClass }
    );
    const instance = modalRef.componentInstance as ExportEntityListModalComponent;

    instance.colDefs = cols;
    instance.exportColDefs = cols.filter((cd, i, self) =>
      cd.headerName != null &&
      i === self.findIndex(c => c.headerName === cd.headerName)
    );
    instance.numberOfCompanies = allRows.length;
    instance.exportType = ExportTypeEnum.PDF;

    instance.confirm
      .pipe(take(1))
      .subscribe((columnsForExport: string[]) => {
        if (!columnsForExport?.length) return;

        const currentRows = allRows.filter(r => !r.cessationDate);
        const pastRows = allRows.filter(r => r.cessationDate);

        const tableBody: any[][] = [];

        tableBody.push([
          {
            content: 'Current Officeholders',
            colSpan: columnsForExport.length,
            styles: {
              halign: 'center',
              fillColor: [240, 230, 230],
              fontStyle: 'bold',
              textColor: [0, 0, 0]
            }
          }
        ]);

        currentRows.forEach((row) => {
          const rowData = (columnsForExport ?? []).map(fieldName => {
            switch (fieldName) {
              case 'officeholderName':
                return row.officeholderName ?? '';
              case 'types':
                return (row.types ?? []).map((type) => RelationshipTypeLabels[type]).join(', ') ?? '';
              case 'address.normalizedFullAddress':
                return row.address?.normalizedFullAddress ?? '';
              case 'appointmentDate': {
                const raw = row.appointmentDate;
                const dt = typeof raw === 'string' ? new Date(raw) : raw;
                return dt ? this.formatDate(dt, 'dd MMM yyyy') : '';
              }
              case 'cessationDate':
                return '';
              case 'dob': {
                const raw = row.dob;
                const dt = typeof raw === 'string' ? new Date(raw) : raw;
                return dt ? this.formatDate(dt, 'dd MMM yyyy') : '';
              }
              case 'birthCity': {
                const city = row.birthCity ?? '';
                const country = row.birthCountry ?? '';
                return city && country ? `${ country }, ${ city }` : city || country || '';
              }
              case 'din': {
                const raw = row.din;
                if (raw == null || raw === '') return '';
                const n = Number(raw);
                if (Number.isNaN(n)) return raw?.toString() ?? '';
                return Math.trunc(n).toString();
              }
              default: {
                const anyRow = row as any;
                const rawVal = anyRow[fieldName];
                return rawVal != null ? rawVal.toString() : '';
              }
            }
          });

          const hasAnyValue = rowData.some(cell => cell !== '');
          if (hasAnyValue) {
            tableBody.push(rowData);
          }
        });
        if (pastRows.length > 0) {
          tableBody.push([
            {
              content: 'Past Officeholders',
              colSpan: columnsForExport.length,
              styles: {
                halign: 'center',
                fillColor: [240, 230, 230],
                fontStyle: 'bold',
                textColor: [0, 0, 0]
              }
            }
          ]);

          pastRows.forEach(row => {
            const rowData = columnsForExport.map(fieldName => {
              switch (fieldName) {
                case 'officeholderName':
                  return row.officeholderName ?? '';
                case 'types':
                  return row.types.map((type) => RelationshipTypeLabels[type]).join(', ') ?? '';
                case 'address.normalizedFullAddress':
                  return row.address?.normalizedFullAddress ?? '';
                case 'appointmentDate': {
                  const raw = row.appointmentDate;
                  const dt = typeof raw === 'string' ? new Date(raw) : raw;
                  return dt ? this.formatDate(dt, 'dd MMM yyyy') : '';
                }
                case 'cessationDate': {
                  const raw = row.cessationDate;
                  const dt = typeof raw === 'string' ? new Date(raw) : raw;
                  return dt ? this.formatDate(dt, 'dd MMM yyyy') : '';
                }
                case 'dob': {
                  const raw = row.dob;
                  const dt = typeof raw === 'string' ? new Date(raw) : raw;
                  return dt ? this.formatDate(dt, 'dd MMM yyyy') : '';
                }
                case 'birthCity': {
                  const city = row.birthCity ?? '';
                  const country = row.birthCountry ?? '';
                  return city && country ? `${ country }, ${ city }` : city || country || '';
                }
                case 'din': {
                  const raw = row.din;
                  if (raw == null || raw === '') return '';
                  const n = Number(raw);
                  if (Number.isNaN(n)) return raw.toString();
                  return Math.trunc(n).toString();
                }
                default: {
                  const anyRow = row as any;
                  const rawVal = anyRow[fieldName];
                  return rawVal != null ? rawVal.toString() : '';
                }
              }
            });

            const hasAnyValue = rowData.some(cell => cell !== '');
            if (hasAnyValue) {
              tableBody.push(rowData);
            }
          });
        }
        const doc = new jsPDF({
          orientation: 'landscape',
          unit: 'pt',
          format: 'a4'
        });
        const pageWidth = doc.internal.pageSize.getWidth();

        doc.setFontSize(22);
        doc.setFont('helvetica', 'bold');
        const titleText = 'Officeholders Report';
        const titleX = pageWidth / 2 - doc.getTextDimensions(titleText).w / 2;
        doc.text(titleText, titleX, 40);

        doc.setLineWidth(1);
        doc.line(40, 50, pageWidth - 40, 50);

        const now = new Date();
        const day = now.getDate().toString().padStart(2, '0');
        const month = (now.getMonth() + 1).toString().padStart(2, '0');
        const year = now.getFullYear();
        const asOfText = `As of ${ day }/${ month }/${ year }`;

        doc.setFontSize(14);
        doc.setFont('helvetica', 'normal');
        const asOfW = doc.getTextDimensions(asOfText).w;
        doc.text(asOfText, pageWidth / 2 - asOfW / 2, 68);

        doc.setLineWidth(1);
        doc.line(40, 75, pageWidth - 40, 75);

        autoTable(doc, {
          startY: 100,
          head: [
            columnsForExport.map(fieldName => {
              const cd = cols.find(c => c.field === fieldName)!;
              return cd.headerName as string;
            })
          ],
          body: tableBody,
          styles: {
            fontSize: 10,
            cellPadding: 6,
            valign: 'middle',
            halign: 'left',
            textColor: [0, 0, 0],
            lineColor: [0, 0, 0],
            lineWidth: 0.5
          },
          headStyles: {
            fillColor: [220, 220, 220],
            textColor: [0, 0, 0],
            fontStyle: 'bold',
            halign: 'center'
          },
          margin: { left: 40, right: 40 },
          tableWidth: pageWidth - 80
        });

        const today = formatDate(new Date(), 'dd-MM-yyyy', 'en-US');
        const fileName = `${ companyProfile.name }_Officeholders_${ today }.pdf`;
        doc.save(fileName);
      });
  }

  // =====  Shareholders  =====

  exportShareholders(
    exportType: ExportTypeEnum,
    companyProfile: Company,
    entitySecurityholdersViewComponent: EntitySecurityholdersViewComponent,
    selectedShareholdersViewModels: SecurityholderViewModel[],
    onlySelected
  ): void {
    if (exportType == ExportTypeEnum.EXCEL) {
      this.exportShareholdersExcel(
        companyProfile,
        entitySecurityholdersViewComponent,
        selectedShareholdersViewModels,
        onlySelected
      );
    }
    if (exportType == ExportTypeEnum.PDF) {
      this.exportShareholdersPDF(
        companyProfile,
        entitySecurityholdersViewComponent,
        selectedShareholdersViewModels
      );
    }
  }

  exportShareholdersExcel(companyProfile: Company, entitySecurityholdersViewComponent: EntitySecurityholdersViewComponent, selectedShareholdersViewModels: SecurityholderViewModel[], onlySelected: boolean): void {
    if (!entitySecurityholdersViewComponent) return;

    let shareholders: SecurityholderViewModel[] =
      selectedShareholdersViewModels.length
        ? selectedShareholdersViewModels
        : ((entitySecurityholdersViewComponent?.shareholdersViewModel().shareholders as any[]) ?? []);

    const cols = entitySecurityholdersViewComponent?.colDefs() ?? [];
    const nameField = cols.find(cd => cd.headerName === 'Name')?.field;
    if (nameField) {
      shareholders = shareholders.filter(row => {
        const val = (row as any)[nameField];
        if (Array.isArray(val)) {
          const names = val
            .map((hn: any) => hn.name ?? '')
            .filter((n: string) => n.trim() !== '');
          return names.length > 0;
        }
        const str = val?.toString() ?? '';
        return str.trim() !== '';
      });
    }

    const params: Partial<ExportEntityListModalComponent> = {
      title: 'Export Company Shareholders',
      subTitle: 'shareholders selected'
    };

    const columnsForExport = cols
      .map(cd => cd.field ?? '')
      .filter(f => !!f) as string[];
    const titleRows = excelTitleForReport(
      columnsForExport.length - 1,
      'Shareholders Report'
    );

    this.exportElements(
      shareholders,
      onlySelected,
      cols,
      ExportTypeEnum.EXCEL,
      params,
      entitySecurityholdersViewComponent?.entitySecurityholdersGridService?.gridApi,
      'Shareholders',
      titleRows,
      companyProfile.name,
      (cellParams: ProcessCellForExportParams) => {
        const colId = cellParams.column.getColId();
        const dataNode = cellParams.node?.data as any;
        const rawVal = cellParams.value;

        if (colId === 'beneficialOwner') {
          const boObj = dataNode?.beneficialOwner as Record<string, any[]> | undefined;
          if (!boObj) return '';
          const allOwners = Object.values(boObj)
            .flatMap(arr => arr.map(o => o.details?.beneficialOwner ?? ''))
            .filter(name => name.trim() !== '');
          return allOwners.join(', ');
        }
        if (colId === 'isBeneficialOwner' || colId === 'beneficiallyOwned') {
          return dataNode.isBeneficialOwner ? 'Yes' : 'No';
        }
        if (colId === 'fullyPaid') {
          return dataNode.fullyPaid ? 'Yes' : 'No';
        }
        if (colId === 'addresses') {
          const addrArray = dataNode?.addresses as any[] | undefined;
          if (!addrArray) return '';
          return addrArray
            .map((addr: any) => addr.normalizedFullAddress ?? '')
            .join(', ');
        }
        if (colId === 'number' || colId === '# of Shares') {
          return (rawVal != null && rawVal !== '')
            ? rawVal.toString()
            : '-';
        }
        return rawVal != null ? rawVal.toString() : '';
      }
    );
  }

  exportShareholdersPDF(companyProfile: Company, entitySecurityholdersViewComponent: EntitySecurityholdersViewComponent, selectedShareholdersViewModels: SecurityholderViewModel[]): void {
    const rawRows = (entitySecurityholdersViewComponent?.shareholdersViewModel().shareholders as any[]) ?? [];
    const allRows: SecurityholderViewModel[] = rawRows
      .filter(r => r != null && typeof r === 'object') as SecurityholderViewModel[];

    const rowsToExport = selectedShareholdersViewModels.length
      ? selectedShareholdersViewModels
      : allRows;

    const cols: ColDef[] = entitySecurityholdersViewComponent?.colDefs() ?? [];
    const modalDialogClass = 'export-company-list';
    const modalRef = this.modalFormsService.openModal(
      ExportEntityListModalComponent,
      { modalDialogClass }
    );
    const instance = modalRef.componentInstance as ExportEntityListModalComponent;

    instance.colDefs = cols;
    instance.exportColDefs = cols.filter((cd, i, self) =>
      cd.headerName != null &&
      i === self.findIndex(c => c.headerName === cd.headerName)
    );
    instance.numberOfCompanies = rowsToExport.length;
    instance.exportType = ExportTypeEnum.PDF;
    instance.confirm
      .pipe(take(1))
      .subscribe((columnsForExport: string[]) => {
        if (!columnsForExport?.length) return;
        const selectedColDefs: ColDef[] = cols.filter(cd => {
          if (cd.headerName === 'Name') {
            return true;
          }
          return cd.field != null && columnsForExport.includes(cd.field);
        });
        const nameColIndex = selectedColDefs.findIndex(cd => cd.headerName === 'Name');
        const tableBody: any[][] = [];
        const currentShareholders: any[][] = [];
        const pastShareholders: any[][] = [];

        rowsToExport.map((row) => {
          if (typeof row == 'string') return;

          const shareholderRows = selectedColDefs.map(cd => {
            const fieldName = cd.field;
            const header = cd.headerName ?? '';
            if (header === 'Name') {
              return row.holdersNames.map(hn => hn.name).join(', ');
            }
            if (fieldName === 'holdersNames') {
              return row.holdersNames.map(hn => hn.name).join(', ');
            }
            if (fieldName === 'number' || fieldName === 'numberOfShares') {
              return row.number != null
                ? row.number.toString()
                : '-';
            }
            if (fieldName === 'addresses' || fieldName === 'address') {
              const addrArray = row.addresses as any[] | undefined;
              if (!addrArray || addrArray.length === 0) return '';
              const parts = addrArray.map(addr => {
                if (addr == null) return '';
                if (typeof addr === 'string') {
                  return addr;
                }
                if ('normalizedFullAddress' in addr) {
                  return (addr as any).normalizedFullAddress ?? '';
                }
                if (addr.address && typeof addr.address === 'object' && 'normalizedFullAddress' in addr.address) {
                  return (addr.address as any).normalizedFullAddress ?? '';
                }
                return '';
              });
              return parts.filter((s: string) => !!s).join(', ');
            }
            if (fieldName === 'beneficialOwner') {
              const boObj = row.beneficialOwner as Record<string, any[]> | undefined;
              if (!boObj) return '';
              const owners = Object.values(boObj)
                .flatMap(arr => arr.map(o => o.details?.beneficialOwner ?? ''))
                .filter(name => name.trim() !== '');
              return owners.join(', ');
            }
            if (fieldName === 'isBeneficialOwner' || fieldName === 'beneficiallyOwned') {
              return row.isBeneficialOwner ? 'Yes' : 'No';
            }
            if (fieldName === 'fullyPaid') {
              return row.fullyPaid ? 'Yes' : 'No';
            }
            if (fieldName === 'holding' || fieldName === 'shareClass') {
              return row.holding ?? '';
            }
            if (fieldName === 'number' || fieldName === 'numberOfShares') {
              return row.number != null ? row.number.toString() : '';
            }
            if (fieldName === 'authorisedSignatory') {
              const authArr = row.authorisedSignatory as string[][] | undefined;
              if (!authArr || authArr.length === 0) return '';
              return authArr.map(inner => inner.join(' ')).join('; ');
            }

            if (fieldName === 'holdingDate') {
              const raw = (row.record as any).holdingDate;
              const dt = typeof raw === 'string' ? new Date(raw) : raw;
              return dt ? this.formatDate(dt, 'dd MMM yyyy') : '';
            }
            if (fieldName) {
              const anyRow = row as any;
              const rawVal = anyRow[fieldName];
              return rawVal != null ? rawVal.toString() : '';
            }
            return '';
          });

          if (row.isPastShareholder()) {
            pastShareholders.push(shareholderRows);
          } else {
            currentShareholders.push(shareholderRows);
          }
        });

        if (currentShareholders.length > 0) {
          tableBody.push(
            [{
              content: 'Current Shareholders',
              colSpan: columnsForExport.length,
              styles: {
                halign: 'center',
                fillColor: [240, 230, 230],
                fontStyle: 'bold',
                textColor: [0, 0, 0]
              }
            }],
            ...currentShareholders
          );
        }

        if (pastShareholders.length > 0) {
          tableBody.push(
            [{
              content: 'Past Shareholders',
              colSpan: columnsForExport.length,
              styles: {
                halign: 'center',
                fillColor: [240, 230, 230],
                fontStyle: 'bold',
                textColor: [0, 0, 0]
              }
            }],
            ...pastShareholders
          );
        }

        const doc = new jsPDF({
          orientation: 'landscape',
          unit: 'pt',
          format: 'a4'
        });
        const pageWidth = doc.internal.pageSize.getWidth();

        doc.setFontSize(22);
        doc.setFont('helvetica', 'bold');
        const titleText = 'Shareholders Report';
        const titleX = pageWidth / 2 - doc.getTextDimensions(titleText).w / 2;
        doc.text(titleText, titleX, 40);

        doc.setLineWidth(1);
        doc.line(40, 50, pageWidth - 40, 50);
        const now = new Date();
        const day = now.getDate().toString().padStart(2, '0');
        const month = (now.getMonth() + 1).toString().padStart(2, '0');
        const year = now.getFullYear();
        const asOfText = `As of ${ day }/${ month }/${ year }`;

        doc.setFontSize(14);
        doc.setFont('helvetica', 'normal');
        const asOfW = doc.getTextDimensions(asOfText).w;
        doc.text(asOfText, pageWidth / 2 - asOfW / 2, 68);

        doc.setLineWidth(1);
        doc.line(40, 75, pageWidth - 40, 75);
        const headers: string[] = selectedColDefs.map(cd => cd.headerName as string);
        autoTable(doc, {
          startY: 85,
          head: [headers],
          body: tableBody,
          styles: {
            fontSize: 10,
            cellPadding: 6,
            valign: 'middle',
            halign: 'left',
            textColor: [0, 0, 0],
            lineColor: [0, 0, 0],
            lineWidth: 0.5
          },
          headStyles: {
            fillColor: [220, 220, 220],
            textColor: [0, 0, 0],
            fontStyle: 'bold',
            halign: 'center'
          },
          margin: { left: 40, right: 40 },
          tableWidth: pageWidth - 80,
          columnStyles: {
            [nameColIndex]: { cellWidth: 150 }
          }
        });
        const today = formatDate(new Date(), 'dd-MM-yyyy', 'en-US');
        const fileName = `${ companyProfile.name }_Shareholders_${ today }.pdf`;
        doc.save(fileName);
      });
  }

  // =====  Documents  =====


  exportDocuments(
    exportType: ExportTypeEnum,
    companyProfile: Company,
    entityProfileDocumentsComponent: EntityProfileDocumentsComponent,
    selectedDocuments: DocumentRecord[],
    onlySelected: boolean
  ): void {
    if (exportType == ExportTypeEnum.EXCEL) {
      this.exportDocumentsExcel(
        companyProfile,
        entityProfileDocumentsComponent,
        selectedDocuments,
        onlySelected
      );
    }
    if (exportType == ExportTypeEnum.PDF) {
      this.exportDocumentsPDF(
        companyProfile,
        entityProfileDocumentsComponent,
        selectedDocuments,
      );
    }
  }

  exportDocumentsExcel(
    companyProfile: Company,
    entityProfileDocumentsComponent: EntityProfileDocumentsComponent,
    selectedDocuments: DocumentRecord[],
    onlySelected: boolean
  ): void {
    if (!entityProfileDocumentsComponent) return;

    const params: Partial<ExportEntityListModalComponent> = {
      title: 'Export Documents List',
      subTitle: 'documents selected'
    };

    const colDefs: ColDef[] =
      entityProfileDocumentsComponent?.colDefs() ?? [];
    const columnsForExport = colDefs
      .map(cd => cd.field ?? '')
      .filter(f => !!f) as string[];

    const titleRows = excelTitleForReport(
      columnsForExport.length - 1,
      'Documents Report'
    );

    this.exportElements(
      selectedDocuments,
      onlySelected,
      colDefs,
      ExportTypeEnum.EXCEL,
      params,
      entityProfileDocumentsComponent?.entityProfileDocumentsGridService?.gridApi,
      'Documents',
      titleRows,
      companyProfile.name,
      (cellParams: ProcessCellForExportParams) => {
        const colId = cellParams.column.getColId();
        const dataNode = cellParams.node?.data as any;

        const raw = cellParams.value;
        let asDate: Date | null = null;

        if (raw instanceof Date || (typeof raw === 'string' && !isNaN(Date.parse(raw)))) {
          const dt = raw instanceof Date ? raw : new Date(raw);
          return this.formatDate(dt);
        }
        if (asDate) {
          return formatDate(asDate, 'yyyy-MM-dd', 'en-US');
        }

        if (colId === 'status') {
          return documentStatusLabels[dataNode.status as DocumentStatusEnum] || '';
        }
        if (colId === 'asicStatus') {
          const rawValue = dataNode.asicStatus as AsicStatus | undefined;
          switch (rawValue) {
            case AsicStatus.NotLodged:
              return 'Not Lodged';
            case AsicStatus.WaitingForASIC:
              return 'Waiting For ASIC';
            case AsicStatus.FutureLodgement:
              return 'Future Lodgement';
            case AsicStatus.ManualProcessing:
              return 'Manual Processing';
            default:
              return AsicStatus[rawValue ?? -1] ?? '-';
          }
        }
        if (colId === 'signingStatus') {
          const rawValue = dataNode.signingStatus as SigningStatus | undefined;
          switch (rawValue) {
            case SigningStatus.ReadyToSend:
              return 'Ready to Send';
            case SigningStatus.Created:
            case SigningStatus.Sent:
              return 'Sent';
            case SigningStatus.Signed:
              return 'Signed';
            case SigningStatus.MarkedAsSigned:
              return 'Marked as Signed';
            case SigningStatus.MarkedAsSent:
              return 'Marked as Sent';
            case SigningStatus.Rejected:
              return 'Rejected';
            case SigningStatus.SentViaEmail:
              return 'Sent via Email';
            default:
              return '-';
          }
        }
        if (colId === 'lateFee') {
          const num = Number(raw);
          return isNaN(num) ? '' : `$${ num }`;
        }
        const rawVal = (dataNode as any)[colId];
        return rawVal != null ? rawVal.toString() : '';
      }
    );
  }

  exportDocumentsPDF(
    companyProfile: Company,
    entityProfileDocumentsComponent: EntityProfileDocumentsComponent,
    selectedDocuments: DocumentRecord[],
  ): void {
    if (!entityProfileDocumentsComponent) return;

    const colDefs: ColDef[] = entityProfileDocumentsComponent?.colDefs() ?? [];

    const modalDialogClass = 'export-company-list';
    const modalRef = this.modalFormsService.openModal(
      ExportEntityListModalComponent,
      { modalDialogClass }
    );
    const instance = modalRef.componentInstance as ExportEntityListModalComponent;

    instance.colDefs = colDefs;
    instance.exportColDefs = colDefs.filter((cd, i, self) =>
      cd.headerName != null &&
      i === self.findIndex(c => c.headerName === cd.headerName)
    );
    instance.numberOfCompanies = selectedDocuments.length;
    instance.exportType = ExportTypeEnum.PDF;

    instance.confirm
      .pipe(take(1))
      .subscribe((columnsForExport: string[]) => {
        if (!columnsForExport?.length) return;

        const selectedColDefs: ColDef[] = colDefs.filter(cd => {
          return cd.field != null && columnsForExport.includes(cd.field);
        });

        const tableBody: any[][] = selectedDocuments.map(doc => {
          return selectedColDefs.map(cd => {
            const fieldName = cd.field!;
            const header = cd.headerName?.toLowerCase() ?? '';
            const dataNode = doc as any;
            const raw = dataNode[fieldName];

            if (header.includes('date') || header.includes('deadline')) {
              let dt: Date | null = null;
              if (raw instanceof Date) {
                dt = raw;
              } else if (typeof raw === 'string' && !isNaN(Date.parse(raw))) {
                dt = new Date(raw);
              }
              return dt ? this.formatDate(dt) : '';
            }
            if (fieldName === 'status') {
              const enumVal = dataNode.status as DocumentStatusEnum | undefined;
              return documentStatusLabels[enumVal ?? -1] || '';
            }
            if (fieldName === 'lateFee') {
              const num = Number(raw);
              return isNaN(num) ? '' : `$${ num }`;
            }
            if (fieldName === 'asicStatus') {
              const rawVal = dataNode.asicStatus as AsicStatus | undefined;
              switch (rawVal) {
                case AsicStatus.NotLodged:
                  return 'Not Lodged';
                case AsicStatus.WaitingForASIC:
                  return 'Waiting For ASIC';
                case AsicStatus.FutureLodgement:
                  return 'Future Lodgement';
                case AsicStatus.ManualProcessing:
                  return 'Manual Processing';
                default:
                  return AsicStatus[rawVal ?? -1] ?? '-';
              }
            }

            if (fieldName === 'signingStatus') {
              const rawVal = dataNode.signingStatus as SigningStatus | undefined;
              switch (rawVal) {
                case SigningStatus.ReadyToSend:
                  return 'Ready to Send';
                case SigningStatus.Created:
                case SigningStatus.Sent:
                  return 'Sent';
                case SigningStatus.Signed:
                  return 'Signed';
                case SigningStatus.MarkedAsSigned:
                  return 'Marked as Signed';
                case SigningStatus.MarkedAsSent:
                  return 'Marked as Sent';
                case SigningStatus.Rejected:
                  return 'Rejected';
                case SigningStatus.SentViaEmail:
                  return 'Sent via Email';
                default:
                  return '-';
              }
            }

            if (fieldName === 'address' || fieldName === 'addresses') {
              const addrArray = dataNode.addresses as any[] | undefined;
              if (!addrArray || addrArray.length === 0) return '';
              const parts = addrArray.map(addr => {
                if (addr == null) return '';
                if (typeof addr === 'string') return addr;
                if ('normalizedFullAddress' in addr) {
                  return (addr as any).normalizedFullAddress ?? '';
                }
                if (addr.address && typeof addr.address === 'object' && 'normalizedFullAddress' in addr.address) {
                  return (addr.address as any).normalizedFullAddress ?? '';
                }
                return '';
              });
              return parts.filter((s: string) => !!s).join(', ');
            }

            return raw != null ? raw.toString() : '';
          });
        });

        const docPdf = new jsPDF({
          orientation: 'landscape',
          unit: 'pt',
          format: 'a4'
        });
        const pageWidth = docPdf.internal.pageSize.getWidth();

        docPdf.setFontSize(22);
        docPdf.setFont('helvetica', 'bold');
        const titleText = 'Documents Report';
        const titleX = pageWidth / 2 - docPdf.getTextDimensions(titleText).w / 2;
        docPdf.text(titleText, titleX, 40);

        docPdf.setLineWidth(1);
        docPdf.line(40, 50, pageWidth - 40, 50);

        const now = new Date();
        const dd = now.getDate().toString().padStart(2, '0');
        const mm = (now.getMonth() + 1).toString().padStart(2, '0');
        const yyyy = now.getFullYear();
        const asOfText = `As of ${ dd }/${ mm }/${ yyyy }`;

        docPdf.setFontSize(14);
        docPdf.setFont('helvetica', 'normal');
        const asOfW = docPdf.getTextDimensions(asOfText).w;
        docPdf.text(asOfText, pageWidth / 2 - asOfW / 2, 68);

        docPdf.setLineWidth(1);
        docPdf.line(40, 75, pageWidth - 40, 75);

        const headers: string[] = selectedColDefs.map(cd => cd.headerName as string);
        const lateFeeColIndex = headers.indexOf('Late Fee');
        const descriptionColIndex = headers.indexOf('Description');
        autoTable(docPdf, {
          startY: 85,
          head: [headers],
          body: tableBody,
          styles: {
            fontSize: 10,
            cellPadding: 6,
            valign: 'middle',
            halign: 'left',
            textColor: [0, 0, 0],
            lineColor: [0, 0, 0],
            lineWidth: 0.5
          },
          headStyles: {
            fillColor: [220, 220, 220],
            textColor: [0, 0, 0],
            fontStyle: 'bold',
            halign: 'center'
          },
          margin: { left: 40, right: 40 },
          tableWidth: pageWidth - 80,
          columnStyles: {
            ...(lateFeeColIndex >= 0 && {
              [lateFeeColIndex]: { minCellWidth: 50 }
            }),
            ...(descriptionColIndex >= 0 && {
              [descriptionColIndex]: { maxCellWidth: 140 }
            })
          } as Record<string, Partial<Styles>>
        });
        const today = formatDate(new Date(), 'dd-MM-yyyy', 'en-US');
        const fileName = `${ companyProfile.name }_Documents_${ today }.pdf`;
        docPdf.save(fileName);
      });
  }
}
